import { useState, useEffect, useRef } from 'react';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { Bell, BellOff, Check, CheckCheck, X, ExternalLink, Trophy, Dumbbell, AlertTriangle, MessageSquare, Sparkles } from 'lucide-react';
import Loader2 from '@/components/brand/Loader2';
import { useNavigate } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NotificationInbox = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  // Notificación seleccionada para abrir en popup (Dialog).
  const [activeNotification, setActiveNotification] = useState(null);
  const closeBtnRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      loadNotifications();
    }
  }, [isOpen]);

  // Accesibilidad del panel: enfocar el cierre al abrir y cerrar con ESC.
  // Cuando el popup (Dialog de Radix) está abierto, Radix maneja su propio ESC,
  // así que aquí solo cerramos el panel si NO hay notificación abierta.
  useEffect(() => {
    if (!isOpen) return undefined;
    closeBtnRef.current?.focus();
    const onKey = (e) => {
      if (e.key === 'Escape' && !activeNotification) onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, activeNotification, onClose]);

  const loadNotifications = async () => {
    try {
      const response = await axios.get(`${API}/notifications/inapp`);
      setNotifications(response.data.notifications || []);
      setUnreadCount(response.data.unread_count || 0);
    } catch (error) {
      console.error('Error loading notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await axios.patch(`${API}/notifications/inapp/${notificationId}/read`);
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, read_at: new Date().toISOString() } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await axios.patch(`${API}/notifications/inapp/read-all`);
      setNotifications(prev => 
        prev.map(n => ({ ...n, read_at: n.read_at || new Date().toISOString() }))
      );
      setUnreadCount(0);
      toast.success('Todas las notificaciones marcadas como leídas');
    } catch (error) {
      toast.error('Error al marcar notificaciones');
    }
  };

  const handleNotificationClick = (notification) => {
    // Abrimos popup con el contenido completo en lugar de navegar
    // directo. El CTA "Ir a la sección" navega cuando el usuario decide.
    if (!notification.read_at) {
      markAsRead(notification.id);
    }
    setActiveNotification(notification);
  };

  const handleNotificationCTA = () => {
    if (activeNotification?.action_url) {
      const url = activeNotification.action_url;
      setActiveNotification(null);
      onClose();
      navigate(url);
    }
  };

  const getNotificationIcon = (type) => {
    const icons = {
      'PLAN_APPROVED': { icon: Sparkles, color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
      'SESSION_COMPLETED': { icon: Trophy, color: 'text-amber-400', bg: 'bg-amber-500/10' },
      'WORKOUT_DAY_START': { icon: Dumbbell, color: 'text-primary', bg: 'bg-primary/10' },
      'PREWORKOUT_REMINDER': { icon: Bell, color: 'text-purple-400', bg: 'bg-purple-500/10' },
      'INJURY_ALERT': { icon: AlertTriangle, color: 'text-red-400', bg: 'bg-red-500/10' },
      'COACH_MESSAGE': { icon: MessageSquare, color: 'text-blue-400', bg: 'bg-blue-500/10' },
      'ACHIEVEMENT': { icon: Trophy, color: 'text-amber-400', bg: 'bg-amber-500/10' },
    };
    return icons[type] || { icon: Bell, color: 'text-zinc-400', bg: 'bg-zinc-500/10' };
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Ahora';
    if (diffMins < 60) return `${diffMins}m`;
    if (diffHours < 24) return `${diffHours}h`;
    if (diffDays < 7) return `${diffDays}d`;
    return date.toLocaleDateString('es-MX', { month: 'short', day: 'numeric' });
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40 bg-black/50"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Panel — ancho compacto. Click en cada notificación abre un
          popup con el cuerpo completo (ver Dialog al final). */}
      <div
        className="fixed right-0 top-0 bottom-0 w-full max-w-md z-50 bg-zinc-950 border-l border-zinc-800 shadow-2xl"
        data-testid="notification-inbox"
        role="dialog"
        aria-modal="true"
        aria-label="Notificaciones"
      >
        {/* Header */}
        <div className="sticky top-0 bg-zinc-950 border-b border-zinc-800 p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-sm bg-primary/10 flex items-center justify-center">
              <Bell className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h2 className="font-teko text-xl text-white">NOTIFICACIONES</h2>
              {unreadCount > 0 && (
                <span className="text-xs text-primary">{unreadCount} sin leer</span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {unreadCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={markAllAsRead}
                className="text-zinc-400 hover:text-white text-xs"
              >
                <CheckCheck className="w-4 h-4 mr-1" />
                Marcar todo
              </Button>
            )}
            <Button
              ref={closeBtnRef}
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="text-zinc-400 hover:text-white"
              aria-label="Cerrar notificaciones"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>
        </div>

        {/* Notifications List */}
        <div className="overflow-y-auto h-[calc(100dvh-73px)]">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <Loader2 className="w-6 h-6 text-primary animate-spin" />
            </div>
          ) : notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-center px-6">
              <div className="w-16 h-16 rounded-full bg-zinc-900 flex items-center justify-center mb-4">
                <BellOff className="w-8 h-8 text-zinc-600" />
              </div>
              <p className="text-zinc-500 text-sm">No tienes notificaciones</p>
              <p className="text-zinc-600 text-xs mt-1">
                Las actualizaciones de tu plan y entrenamientos aparecerán aquí
              </p>
            </div>
          ) : (
            <div className="divide-y divide-zinc-800/50">
              {notifications.map((notification) => {
                const iconData = getNotificationIcon(notification.type);
                const IconComponent = iconData.icon;
                const isUnread = !notification.read_at;

                return (
                  <button
                    key={notification.id}
                    onClick={() => handleNotificationClick(notification)}
                    className={`w-full p-4 text-left transition-colors hover:bg-zinc-900/50 ${
                      isUnread ? 'bg-primary/5' : ''
                    }`}
                    data-testid={`notification-${notification.id}`}
                  >
                    <div className="flex gap-3">
                      {/* Icon */}
                      <div className={`flex-shrink-0 w-10 h-10 rounded-sm ${iconData.bg} flex items-center justify-center`}>
                        <IconComponent className={`w-5 h-5 ${iconData.color}`} />
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <h3 className={`text-sm font-medium truncate ${isUnread ? 'text-white' : 'text-zinc-300'}`}>
                            {notification.title}
                          </h3>
                          <span className="text-xs text-zinc-500 flex-shrink-0">
                            {formatTime(notification.created_at)}
                          </span>
                        </div>
                        <p className={`text-xs mt-0.5 line-clamp-2 ${isUnread ? 'text-zinc-400' : 'text-zinc-500'}`}>
                          {notification.body}
                        </p>
                        
                        {notification.action_url && (
                          <div className="flex items-center gap-1 mt-2 text-primary text-xs">
                            <ExternalLink className="w-3 h-3" />
                            <span>Ver detalles</span>
                          </div>
                        )}
                      </div>

                      {/* Unread indicator */}
                      {isUnread && (
                        <div className="flex-shrink-0 w-2 h-2 rounded-full bg-primary mt-2" />
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Popup centrado con el contenido completo de la notificación */}
      <Dialog
        open={!!activeNotification}
        onOpenChange={(open) => { if (!open) setActiveNotification(null); }}
      >
        <DialogContent className="bg-zinc-950 border-zinc-800 max-w-lg">
          {activeNotification && (() => {
            const iconData = getNotificationIcon(activeNotification.type);
            const Icon = iconData.icon;
            return (
              <>
                <DialogHeader>
                  <div className="flex items-start gap-3">
                    <div className={`flex-shrink-0 w-10 h-10 rounded-sm ${iconData.bg} flex items-center justify-center`}>
                      <Icon className={`w-5 h-5 ${iconData.color}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <DialogTitle className="text-white font-teko text-xl uppercase">
                        {activeNotification.title}
                      </DialogTitle>
                      <p className="text-zinc-500 text-xs mt-1">
                        {new Date(activeNotification.created_at).toLocaleString('es-MX', {
                          dateStyle: 'medium',
                          timeStyle: 'short',
                        })}
                      </p>
                    </div>
                  </div>
                </DialogHeader>
                <div className="py-2">
                  <p className="text-zinc-300 text-sm whitespace-pre-wrap break-words">
                    {activeNotification.body}
                  </p>
                </div>
                <DialogFooter>
                  <Button
                    variant="ghost"
                    onClick={() => setActiveNotification(null)}
                    className="text-zinc-400"
                  >
                    Cerrar
                  </Button>
                  {activeNotification.action_url && (
                    <Button onClick={handleNotificationCTA} className="btn-primary">
                      <ExternalLink className="w-4 h-4 mr-2" />
                      Ir a la sección
                    </Button>
                  )}
                </DialogFooter>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default NotificationInbox;
